/**
 * active-editable-controller.js — the SINGLE write path into
 * `active-editable-store.js`.
 *
 * Deliberately shaped like `ui/selection-controller.js`, handler for handler, so
 * the two can be read side by side. The store is policy-free; everything that
 * decides whether a click BECOMES the active editable lives here.
 *
 * ── Inputs and why each one ───────────────────────────────────────────────
 *   iframe:element-clicked          set — the only thing that ever selects one
 *   iframe:section-clicked          clear — a section click deselects the
 *                                   element, which is what `glass-panels.js`
 *                                   `_onSectionClicked` already does
 *   iframe:selected-editable-cleared clear — the explicit signal, emitted by the
 *                                   shell (delete/escape/full render), the glass
 *                                   panels, the breadcrumb and the inert-family panel
 *   iframe:canvas-clicked           clear — belt and braces. `section-deselected`
 *                                   covers the normal path, but the selection
 *                                   controller only echoes it when a section WAS
 *                                   selected; a canvas click always means nothing
 *                                   is selected, so it can never clear wrongly
 *   section-deselected              clear
 *   state-changed                   prune / reset on the same rules the section
 *                                   controller uses
 *
 * ── Pick mode consumes; paint mode does NOT ───────────────────────────────
 * Pick mode (AI context picker) eats the click entirely — the section is tagged
 * as agent context and nothing becomes selected. `selection-controller` gates on
 * exactly this and so does `element-selection.js`.
 *
 * Paint mode is the opposite and it is easy to get wrong: the format painter
 * applies the style and then lets the element be selected by the normal flow.
 * `shell.js`'s own comment says so — "the painter handler runs first so the
 * panel registry's selection effect still fires after ... the painted element is
 * naturally re-selected by the existing flow". A controller that gated on paint
 * mode would leave this store disagreeing with every other surface after a
 * paint. So: pick mode returns, paint mode falls through.
 *
 * ── Additive, on purpose ──────────────────────────────────────────────────
 * `glass-panels.js`, `ui/property-panel.js` and `glass-resize-handles.js` still
 * keep their own private copies of this value and are NOT touched here.
 * Converging them onto this store is a follow-on; doing it in the same phase
 * that introduces the store would mean changing four surfaces at once with no
 * way to attribute a regression.
 */

import { eventBus } from '../core/events.js';
import { store } from '../core/state.js';
import { sectionOrderForPage } from '../shared/section-order.js';
import { isPickModeActive } from './ai-context-picker.js';
import { activeEditable } from './active-editable-store.js';

let _installed = false;
let _lastActivePage = null;

/** Every instance id that can legally own an editable on the current page. */
function validInstanceIds() {
  return new Set(sectionOrderForPage(store.getState()));
}

// ── policy ──────────────────────────────────────────────────────────────────

function _onElementClicked(data) {
  // Pick mode CONSUMES the click. Same guard, same shape, as
  // selection-controller._onElementClicked.
  if (isPickModeActive() && data?.instanceId && (data?.targetRef || data?.eid)) return;
  // A payload missing either half of the identity clears rather than storing a
  // half-identity — the store enforces this too, but doing it here keeps the
  // reason accurate in the notification.
  activeEditable.set(data, { source: 'element-clicked' });
}

function _onSectionClicked() {
  activeEditable.clear({ source: 'section-clicked', reason: 'section-selected' });
}

function _onSelectedEditableCleared(data) {
  activeEditable.clear({
    source: 'selected-editable-cleared',
    reason: data?.reason || 'explicit'
  });
}

function _onCanvasClicked() {
  activeEditable.clear({ source: 'canvas-clicked' });
}

function _onDeselected() {
  activeEditable.clear({ source: 'section-deselected' });
}

/**
 * Content churn. Mirrors `selection-controller._onStateChanged`:
 *
 *  - an active-page change RESETS rather than prunes, because an editable can
 *    stay technically valid across a page switch (shared nav/footer live on
 *    every page) while no longer being what the user is looking at;
 *  - otherwise, an editable whose owning section is gone is stale and clears.
 *
 * This is the sanctioned use of `state-changed`. The standing "do not
 * invalidate on state-changed" rule is about expensive caches that must survive
 * a transaction scrub; pruning a selection against reality is what the section
 * controller has always done here, and disagreeing with it would be the bug.
 */
function _onStateChanged() {
  const activePage = store.getState()?.activePage ?? null;
  if (_lastActivePage !== null && activePage !== _lastActivePage) {
    _lastActivePage = activePage;
    activeEditable.clear({ source: 'state-changed', reason: 'active-page-changed' });
    return;
  }
  _lastActivePage = activePage;
  const held = activeEditable.getInstanceId();
  if (!held) return;
  if (!validInstanceIds().has(held)) {
    activeEditable.clear({ source: 'state-changed', reason: 'section-removed' });
  }
}

// ── lifecycle ───────────────────────────────────────────────────────────────

/**
 * Install the controller. Idempotent — a second call is a no-op rather than a
 * second set of listeners.
 * @returns {() => void} uninstall
 */
export function initActiveEditableController() {
  if (_installed) return () => {};
  _installed = true;
  _lastActivePage = store.getState()?.activePage ?? null;

  const offs = [
    eventBus.on('iframe:element-clicked', _onElementClicked),
    eventBus.on('iframe:section-clicked', _onSectionClicked),
    eventBus.on('iframe:selected-editable-cleared', _onSelectedEditableCleared),
    eventBus.on('iframe:canvas-clicked', _onCanvasClicked),
    eventBus.on('section-deselected', _onDeselected),
    eventBus.on('state-changed', _onStateChanged)
  ];

  return () => {
    for (const off of offs) if (typeof off === 'function') off();
    _installed = false;
  };
}

/**
 * Identity-boundary reset — project restore, tab-guard promotion, start-over.
 * Exported so callers clear the store without writing it themselves; every
 * mutation stays inside this module.
 */
export function resetActiveEditable(reason) {
  activeEditable.clear({ source: 'shell', reason });
}

/** Test hook — tears the singleton down so a suite can install a fresh one. */
export function _resetActiveEditableControllerForTests() {
  _installed = false;
  _lastActivePage = null;
  activeEditable.clear({ source: 'test-reset' });
}
